import {
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Check, ChevronRight, ExpandLess, ExpandMore, MoreVert } from '@mui/icons-material'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { com, Nullable } from '@eidu/form'
import type { Tokens } from 'marked'
import { marked } from 'marked'
import { type AssistantUsage, sendAssistantChat, type ToolCallSummary } from '../../api/assistant/sendAssistantPrompt'
import { clearMcpStatusCache, getMcpStatus } from '../../api/assistant/mcpStatus'
import { GLOBAL_ASSISTANT_STATIC_PROMPT } from '../../api/assistant/prompts/globalAssistant'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import createEntityToCreate from '../../pages/entity/createEntityToCreate'
import postEntity from '../../api/entity/data/postEntity'
import patchEntity from '../../api/entity/data/patchEntity'
import deleteEntity from '../../api/entity/data/deleteEntity'
import Patch from '../../api/entity/data/Patch'
import { draftFieldValueToFieldValue } from '../../domain/entity/DraftFieldValue'
import { useSnackBar } from '../feedback/SnackBarProvider'
import { type AssistantEditorValueUpdate, useAssistantContext } from './AssistantContext'
import { useUserSettings } from '../../io/context/UserSettings'
import UserSettingsDialog from '../layout/UserSettingsDialog'
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import ExternalValueKey = com.eidu.sharedlib.entity.user.ExternalValueKey
import FieldType = com.eidu.sharedlib.entity.field.FieldType

type AssistantAction =
  | {
      type: 'create-entity'
      entityTypeId: string
      values: Record<string, unknown>
      summary?: string
      reason?: string
    }
  | {
      type: 'update-entity'
      entityId: string
      entityTypeId?: string
      patch: Record<string, unknown>
      summary?: string
      reason?: string
    }
  | {
      type: 'delete-entity'
      entityId: string
      summary?: string
      reason?: string
    }

type ActionStatus = {
  status: 'idle' | 'running' | 'success' | 'error'
  error?: string
}

type ParsedAssistantPayload = {
  userMessage: string
  editorValues: AssistantEditorValueUpdate[]
  actions: AssistantAction[]
}

type AssistantDebugSession = {
  promptContext: string
  systemPrompt: string
  requestPayload?: unknown
  responsePayload?: unknown
  assistantMessage?: string
  toolCallsRequested?: ToolCallSummary[]
  toolCallsCompleted?: ToolCallSummary[]
}

type AssistantMessageDebug = {
  session?: AssistantDebugSession
  error?: string
}

type AssistantMessage = {
  id: string
  role: 'user' | 'assistant'
  text: string
  variant?: 'intermediate' | 'tool-request' | 'tool-result' | 'error'
  elapsedMs?: number
  costLabel?: string
  actions?: AssistantAction[]
  editorValues?: AssistantEditorValueUpdate[]
  actionStatus?: ActionStatus
  debug?: AssistantMessageDebug
}

type ModelOption = {
  id: string
  label: string
  model: string
  reasoningMode?: 'low' | 'medium' | 'high' | 'xhigh'
}

const ASSISTANT_DEFAULT_MODEL = 'gpt-5.5'
const ASSISTANT_DEFAULT_REASONING: ModelOption['reasoningMode'] = 'low'
const REASONING_MODES = ['xhigh', 'high', 'medium', 'low'] as const
const ASSISTANT_MODELS = ['gpt-5.5', 'gpt-5.4-mini', 'gpt-5.4', 'gpt-5.4-nano'] as const

const createModelOptions = (): ModelOption[] =>
  ASSISTANT_MODELS.flatMap((model) => [
    ...REASONING_MODES.map((reasoningMode) => ({
      id: `${model}::${reasoningMode}`,
      label: `${model} (reasoning: ${reasoningMode})`,
      model,
      reasoningMode,
    })),
    { id: `${model}::none`, label: model, model },
  ])

type AssistentProps = {
  children: ReactNode
  drawerWidth?: number
  showStatus?: boolean
  debugMode?: boolean
}

const DEFAULT_DRAWER_WIDTH = 240
const ASSISTANT_MIN_RATIO = 0.1
const ASSISTANT_MAX_RATIO = 0.8
const ASSISTANT_DIVIDER_SIZE_PX = 12

const createMessageId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeEditorValues = (input: unknown): AssistantEditorValueUpdate[] => {
  if (Array.isArray(input)) {
    return input
      .filter(isPlainObject)
      .map((entry): AssistantEditorValueUpdate | null => {
        const fieldId = typeof entry.fieldId === 'string' ? entry.fieldId : undefined
        const fieldName = typeof entry.fieldName === 'string' ? entry.fieldName : undefined
        if (!fieldId && !fieldName) return null
        const update: AssistantEditorValueUpdate = { value: entry.value }
        if (fieldId) update.fieldId = fieldId
        if (fieldName) update.fieldName = fieldName
        return update
      })
      .filter((entry): entry is AssistantEditorValueUpdate => entry !== null)
  }
  if (isPlainObject(input)) {
    return Object.entries(input).map(([fieldId, value]) => ({ fieldId, value }))
  }
  return []
}

const normalizeActions = (input: unknown): AssistantAction[] => {
  if (!Array.isArray(input)) return []
  const actions: AssistantAction[] = []
  input.forEach((entry) => {
    if (!isPlainObject(entry) || typeof entry.type !== 'string') return
    const summary = typeof entry.summary === 'string' ? entry.summary : undefined
    const reason = typeof entry.reason === 'string' ? entry.reason : undefined
    switch (entry.type) {
      case 'create-entity': {
        if (typeof entry.entityTypeId !== 'string') return
        const values = isPlainObject(entry.values) ? entry.values : {}
        actions.push({ type: 'create-entity', entityTypeId: entry.entityTypeId, values, summary, reason })
        return
      }
      case 'update-entity': {
        if (typeof entry.entityId !== 'string') return
        const patch = isPlainObject(entry.patch) ? entry.patch : {}
        const entityTypeId = typeof entry.entityTypeId === 'string' ? entry.entityTypeId : undefined
        actions.push({ type: 'update-entity', entityId: entry.entityId, entityTypeId, patch, summary, reason })
        return
      }
      case 'delete-entity': {
        if (typeof entry.entityId !== 'string') return
        actions.push({ type: 'delete-entity', entityId: entry.entityId, summary, reason })
        return
      }
      default:
        return
    }
  })
  return actions
}

const ASSET_PLACEHOLDER_PATTERN = /asset:\/\/new_(\d+)/g

const replaceAssetPlaceholdersInString = (value: string, mapping: Map<number, string>) =>
  value.replace(ASSET_PLACEHOLDER_PATTERN, (match, indexValue) => {
    const index = Number(indexValue)
    if (!Number.isFinite(index)) return match
    const resolved = mapping.get(index)
    return resolved ? `asset://${resolved}` : match
  })

const replacePlaceholdersInStringRaw = (value: string, mapping: Map<number, string>) =>
  value.replace(ASSET_PLACEHOLDER_PATTERN, (match, indexValue) => {
    const index = Number(indexValue)
    if (!Number.isFinite(index)) return match
    return mapping.get(index) ?? match
  })

const replacePlaceholdersInValue = (value: unknown, mapping: Map<number, string>): unknown => {
  if (typeof value === 'string') return replaceAssetPlaceholdersInString(value, mapping)
  if (Array.isArray(value)) return value.map((entry) => replacePlaceholdersInValue(entry, mapping))
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, replacePlaceholdersInValue(entry, mapping)])
    )
  }
  return value
}

const replacePlaceholdersInEditorValues = (values: AssistantEditorValueUpdate[], mapping: Map<number, string>) =>
  values.map((entry) => ({
    ...entry,
    value: replacePlaceholdersInValue(entry.value, mapping),
  }))

const replacePlaceholdersInAction = (action: AssistantAction, mapping: Map<number, string>): AssistantAction => {
  switch (action.type) {
    case 'create-entity':
      return action
    case 'update-entity':
      return {
        ...action,
        entityId: replacePlaceholdersInStringRaw(action.entityId, mapping),
        entityTypeId: action.entityTypeId ? replacePlaceholdersInStringRaw(action.entityTypeId, mapping) : undefined,
        patch: replacePlaceholdersInValue(action.patch, mapping) as Record<string, unknown>,
      }
    case 'delete-entity':
      return {
        ...action,
        entityId: replacePlaceholdersInStringRaw(action.entityId, mapping),
      }
    default:
      return action
  }
}

type TokenPricing = {
  modelPrefix: string
  inputPerMillion: number
  outputPerMillion: number
  approximate?: boolean
}

const TOKEN_PRICING: TokenPricing[] = [
  { modelPrefix: 'gpt-5.4-mini', inputPerMillion: 0.75, outputPerMillion: 4.5 },
  { modelPrefix: 'gpt-5.4-nano', inputPerMillion: 0.2, outputPerMillion: 1.25 },
  { modelPrefix: 'gpt-5.5', inputPerMillion: 5, outputPerMillion: 30 },
  { modelPrefix: 'gpt-5.4', inputPerMillion: 2.5, outputPerMillion: 15 },
]

const resolveTokenPricing = (model?: string) => {
  if (!model) return undefined
  return TOKEN_PRICING.find((entry) => model.startsWith(entry.modelPrefix))
}

const formatUsd = (value: number) => {
  const abs = Math.abs(value)
  const fractionDigits = abs >= 1 ? 2 : abs >= 0.1 ? 3 : abs >= 0.01 ? 4 : abs >= 0.001 ? 5 : 6
  return value.toFixed(fractionDigits)
}

const formatCostLabel = (usage?: AssistantUsage, model?: string) => {
  if (!usage) return undefined
  const pricing = resolveTokenPricing(model)
  if (!pricing) return undefined
  const inputTokens = usage.inputTokens ?? 0
  const outputTokens = usage.outputTokens ?? 0
  if (!Number.isFinite(inputTokens) && !Number.isFinite(outputTokens)) return undefined
  const cost = (inputTokens * pricing.inputPerMillion + outputTokens * pricing.outputPerMillion) / 1_000_000
  if (!Number.isFinite(cost) || cost <= 0) return undefined
  const prefix = pricing.approximate ? '~' : ''
  return `${prefix}$${formatUsd(cost)}`
}

const extractJson = (raw: string) => {
  const start = raw.indexOf('{')
  const end = raw.lastIndexOf('}')
  if (start >= 0 && end > start) return raw.slice(start, end + 1)
  return raw
}

const findJsonObject = (raw: string) => {
  let inString = false
  let escaped = false
  let depth = 0
  let start = -1

  for (let i = 0; i < raw.length; i += 1) {
    const char = raw[i]
    if (inString) {
      if (escaped) {
        escaped = false
        continue
      }
      if (char === '\\') {
        escaped = true
        continue
      }
      if (char === '"') inString = false
      continue
    }

    if (char === '"') {
      inString = true
      continue
    }

    if (char === '{') {
      if (depth === 0) start = i
      depth += 1
      continue
    }

    if (char === '}' && depth > 0) {
      depth -= 1
      if (depth === 0 && start >= 0) {
        const candidate = raw.slice(start, i + 1)
        try {
          JSON.parse(candidate)
          return candidate
        } catch {
          start = -1
        }
      }
    }
  }

  return null
}

const parseAssistantPayload = (raw: string): { payload?: ParsedAssistantPayload; error?: string } => {
  const tryParse = (value: string) => {
    try {
      return JSON.parse(value) as unknown
    } catch {
      return null
    }
  }

  const extractUserMessage = (parsed: unknown, fallback: string): string | undefined => {
    if (typeof parsed === 'string') return parsed
    if (Array.isArray(parsed)) {
      const stringItems = parsed.filter((item) => typeof item === 'string') as string[]
      if (stringItems.length === parsed.length) return stringItems.join('\n')
    }
    if (isPlainObject(parsed)) {
      const candidate =
        (typeof parsed.userMessage === 'string' && parsed.userMessage) ||
        (typeof parsed.responseForUser === 'string' && parsed.responseForUser) ||
        (typeof parsed.user_message === 'string' && parsed.user_message) ||
        (typeof parsed.message === 'string' && parsed.message) ||
        (typeof parsed.text === 'string' && parsed.text) ||
        (typeof parsed.content === 'string' && parsed.content) ||
        (typeof parsed.error === 'string' && parsed.error)
      if (candidate) return candidate
    }
    const trimmedFallback = fallback.trim()
    return trimmedFallback ? trimmedFallback : undefined
  }

  const trimmed = raw.trim()
  const parsed = tryParse(trimmed) ?? tryParse(extractJson(trimmed)) ?? tryParse(findJsonObject(trimmed) ?? '')
  if (parsed === null) {
    return { error: 'Assistant response could not be parsed as JSON.' }
  }

  const userMessage = extractUserMessage(parsed, trimmed)
  if (!userMessage) {
    return { error: 'Assistant response is missing userMessage.' }
  }

  const parsedObject = isPlainObject(parsed) ? parsed : {}

  return {
    payload: {
      userMessage,
      editorValues: normalizeEditorValues(parsedObject.editorValues),
      actions: normalizeActions(parsedObject.actions),
    },
  }
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const assistantMarkdownStyles = {
  '& p': { margin: '0 0 8px' },
  '& p:last-child': { marginBottom: 0 },
  '& ul, & ol': { margin: '0 0 8px', paddingLeft: '1.25rem' },
  '& li': { marginBottom: '4px' },
  '& pre': {
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    padding: '8px',
    borderRadius: 4,
    overflowX: 'auto',
  },
  '& code': {
    fontFamily: '"SFMono-Regular", "SFMono-Regular", "Consolas", "Liberation Mono", "Menlo", monospace',
    fontSize: '0.8rem',
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    padding: '0 4px',
    borderRadius: 4,
  },
  '& pre code': { backgroundColor: 'transparent', padding: 0 },
  '& blockquote': {
    margin: '0 0 8px',
    paddingLeft: '8px',
    borderLeft: '3px solid',
    borderColor: 'divider',
    color: 'text.secondary',
  },
  '& hr': {
    border: 0,
    borderTop: '1px solid',
    borderColor: 'divider',
    margin: '8px 0',
  },
  '& a': { color: 'inherit', textDecoration: 'underline' },
} as const

const Assistent = ({
  children,
  drawerWidth: _drawerWidth = DEFAULT_DRAWER_WIDTH,
  showStatus = false,
  debugMode = false,
}: AssistentProps) => {
  const [assistantOpen, setAssistantOpen] = useState(false)
  const [assistantInput, setAssistantInput] = useState('')
  const [assistantMessages, setAssistantMessages] = useState<AssistantMessage[]>([])
  const [assistantSending, setAssistantSending] = useState(false)
  const [assistantElapsedMs, setAssistantElapsedMs] = useState(0)
  const [assistantToolNotice] = useState<string | null>(null)
  const [assistantMenuAnchor, setAssistantMenuAnchor] = useState<null | HTMLElement>(null)
  const [modelMenuOpen, setModelMenuOpen] = useState(false)
  const [assistantRatio, setAssistantRatio] = useState(0.2)
  const [assistantResizing, setAssistantResizing] = useState(false)
  const [mcpReady, setMcpReady] = useState<boolean | null>(null)
  const [mcpMessage, setMcpMessage] = useState<string>('')
  const [checkingMcp, setCheckingMcp] = useState(false)
  const [debugDialogOpen, setDebugDialogOpen] = useState(false)
  const [debugDialogData, setDebugDialogData] = useState<AssistantMessageDebug | null>(null)
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const assistantContext = useAssistantContext()
  const { showSnackBar } = useSnackBar()
  const { openAiApiKey } = useUserSettings()
  const iAuthContext = useContext<IAuthContext>(AuthContext)
  const authContext = useMemo(() => createAuthContext(iAuthContext), [iAuthContext])

  const containerRef = useRef<HTMLDivElement | null>(null)
  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const assistantInputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null)
  const modelMenuItemRef = useRef<HTMLLIElement | null>(null)
  const activeResizePointerId = useRef<number | null>(null)
  const pendingResizeClientY = useRef<number | null>(null)
  const resizeAnimationFrame = useRef<number | null>(null)
  const previousUserSelect = useRef<string | null>(null)
  const previousCursor = useRef<string | null>(null)
  const assistantSendStartedAt = useRef<number | null>(null)

  const configuredDefaultModel = import.meta.env.VITE_OPENAI_MODEL as string | undefined
  const envDefaultModel = configuredDefaultModel ?? ASSISTANT_DEFAULT_MODEL
  const envDefaultReasoning =
    (import.meta.env.VITE_OPENAI_REASONING as string | undefined) ??
    (configuredDefaultModel ? undefined : ASSISTANT_DEFAULT_REASONING)

  const {
    organization,
    entityTypes,
    hierarchies,
    assetTypesByEntityType,
    assetTypeCatalog,
    currentView,
    editorAdapter,
    entityTypesMap,
  } = assistantContext

  const modelOptions = useMemo<ModelOption[]>(createModelOptions, [])

  const selectedModelDefaultId = useMemo(() => {
    const envReasoning = typeof envDefaultReasoning === 'string' ? envDefaultReasoning.trim() : undefined
    const reasoningCapableModels = ['gpt-5']
    const isReasoningModel = reasoningCapableModels.some((prefix) => envDefaultModel.startsWith(prefix))
    const envReasoningMode: ModelOption['reasoningMode'] =
      isReasoningModel &&
      (envReasoning === 'low' || envReasoning === 'medium' || envReasoning === 'high' || envReasoning === 'xhigh')
        ? envReasoning
        : undefined
    const envOption = modelOptions.find(
      (option) => option.model === envDefaultModel && option.reasoningMode === envReasoningMode
    )
    return envOption?.id ?? modelOptions[0]?.id ?? ''
  }, [envDefaultModel, envDefaultReasoning, modelOptions])

  const [selectedModelId, setSelectedModelId] = useState(() => selectedModelDefaultId)

  const selectedModel = useMemo(() => {
    const match = modelOptions.find((option) => option.id === selectedModelId)
    return match ?? modelOptions.find((option) => option.id === selectedModelDefaultId) ?? modelOptions[0]
  }, [modelOptions, selectedModelDefaultId, selectedModelId])

  const markdownRenderer = useMemo(() => {
    const renderer = new marked.Renderer()
    renderer.html = (token) => escapeHtml((token as Tokens.HTML).text ?? '')
    renderer.image = (token) => {
      const imageToken = token as Tokens.Image
      const alt = escapeHtml(imageToken.text ?? '')
      return alt ? `<span>${alt}</span>` : ''
    }
    return renderer
  }, [])

  const renderAssistantMarkdown = (value: string) =>
    marked.parse(value, {
      gfm: true,
      breaks: true,
      renderer: markdownRenderer,
    }) as string

  const formatElapsedTime = (elapsedMs: number) => {
    const totalSeconds = Math.floor(elapsedMs / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const formatDebugValue = (value: unknown) => {
    if (value === undefined) return 'Not available.'
    if (typeof value === 'string') return value
    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return String(value)
    }
  }

  const openDebugDialog = (message: AssistantMessage) => {
    if (!debugMode || !message.debug) return
    setDebugDialogData(message.debug)
    setDebugDialogOpen(true)
  }

  const closeDebugDialog = () => {
    setDebugDialogOpen(false)
    setDebugDialogData(null)
  }

  const formatToolCallMessage = (
    call: { name: string; args: Record<string, unknown>; error?: string },
    includeError: boolean
  ) => {
    const queryValue = typeof call.args.query === 'string' ? call.args.query.trim() : ''
    const fallbackQuery = call.name.replace(/-/g, ' ')
    const queryText = queryValue || fallbackQuery
    const errorText = includeError && call.error ? ` (failed: ${call.error})` : ''
    return `Query: ${queryText}${errorText}`
  }

  const formatToolErrorMessage = (_call: { error?: string }) => 'Query failed. Please try again.'

  const buildHistoryForPrompt = useCallback(
    (messages: AssistantMessage[]) =>
      messages
        .filter((message) => message.role === 'user' || (message.role === 'assistant' && !message.variant))
        .map((message) => ({ role: message.role, text: message.text })),
    []
  )

  const buildRequestContext = useCallback(
    (userPrompt: string, previousMessages: AssistantMessage[]) => {
      const payload = {
        organization: organization ?? null,
        entityTypes,
        hierarchies,
        assets: {
          byEntityType: assetTypesByEntityType,
          catalog: assetTypeCatalog,
        },
        currentView: currentView ?? null,
        history: buildHistoryForPrompt(previousMessages),
        userPrompt,
      }
      return `Request context (JSON):\n${JSON.stringify(payload, null, 2)}`
    },
    [
      assetTypeCatalog,
      assetTypesByEntityType,
      buildHistoryForPrompt,
      currentView,
      hierarchies,
      organization,
      entityTypes,
    ]
  )

  const updateMessageById = useCallback(
    (messageId: string, updater: (message: AssistantMessage) => AssistantMessage) => {
      setAssistantMessages((previous) =>
        previous.map((message) => (message.id === messageId ? updater(message) : message))
      )
    },
    []
  )

  const requireAuth = useCallback(() => {
    if (!authContext) throw new Error('Authentication context is missing.')
    return authContext
  }, [authContext])

  const resolveEntityTypeId = useCallback(
    (action: AssistantAction) => {
      if (action.type === 'create-entity') return action.entityTypeId
      if (action.type === 'update-entity') {
        if (action.entityTypeId) return action.entityTypeId
        if (
          currentView &&
          (currentView.kind === 'entity-editor' || currentView.kind === 'asset-editor') &&
          currentView.entityId === action.entityId
        ) {
          return currentView.typeId
        }
      }
      return undefined
    },
    [currentView]
  )

  const resolveEntityType = useCallback(
    (entityTypeId: string) => {
      if (!entityTypesMap) throw new Error('Entity types are not available yet.')
      const typeId = entityTypeIdFromString(entityTypeId)
      const entityType = entityTypesMap.get(typeId)
      if (!entityType) throw new Error(`Entity type not found: ${entityTypeId}`)
      return { typeId, entityType }
    },
    [entityTypesMap]
  )

  const buildFieldLookup = (entityType: EntityType) => {
    const fields = entityType.fields.asJsReadonlyArrayView()
    const byId = new Map(fields.map((field) => [field.id.toString(), field] as const))
    const byName = new Map(fields.map((field) => [field.name.trim().toLowerCase(), field] as const))
    const resolveField = (key: string) => byId.get(key) ?? byName.get(key.trim().toLowerCase())
    return { resolveField }
  }

  const toDraftValue = (value: unknown) => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }

  const normalizeValueForField = (fieldType: FieldType, value: unknown) => {
    if (fieldType === FieldType.Reference && typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed.startsWith('asset://')) return trimmed.slice('asset://'.length)
    }
    return value
  }

  const executeAction = useCallback(
    async (action: AssistantAction): Promise<string | undefined> => {
      const auth = requireAuth()
      switch (action.type) {
        case 'create-entity': {
          const { typeId, entityType } = resolveEntityType(action.entityTypeId)
          const { resolveField } = buildFieldLookup(entityType)
          const entries = Object.entries(action.values ?? {})
          const draftValues = entries.map(([key, value]) => {
            const field = resolveField(key)
            if (!field) throw new Error(`Unknown field "${key}" for ${entityType.name}`)
            const normalizedValue = normalizeValueForField(field.type, value)
            return { fieldId: field.id, type: field.type, value: toDraftValue(normalizedValue) }
          })
          const externalValues = new Map<ExternalValueKey, Nullable<string>>()
          const entityToCreate = createEntityToCreate(typeId, draftValues, [], externalValues)
          const newId = await postEntity({ entity: entityToCreate, authContext: auth })
          return newId.toString()
        }
        case 'update-entity': {
          const entityTypeId = resolveEntityTypeId(action)
          if (!entityTypeId) {
            throw new Error('entityTypeId is required for update actions')
          }
          const { entityType } = resolveEntityType(entityTypeId)
          const { resolveField } = buildFieldLookup(entityType)
          const valuesToUpdateByFieldId = new Map()
          Object.entries(action.patch ?? {}).forEach(([key, value]) => {
            const field = resolveField(key)
            if (!field) throw new Error(`Unknown field "${key}" for ${entityType.name}`)
            const normalizedValue = normalizeValueForField(field.type, value)
            const draftValue = { fieldId: field.id, type: field.type, value: toDraftValue(normalizedValue) }
            valuesToUpdateByFieldId.set(field.id, draftFieldValueToFieldValue(draftValue))
          })
          const patch: Patch = {
            valuesToUpdateByFieldId,
            linksToUpdate: new Map(),
            externalValuesToUpdate: new Map(),
          }
          await patchEntity({ id: entityIdFromString(action.entityId), patch, authContext: auth })
          return undefined
        }
        case 'delete-entity': {
          await deleteEntity({ id: entityIdFromString(action.entityId), authContext: auth })
          return undefined
        }
        default:
          return undefined
      }
    },
    [buildFieldLookup, normalizeValueForField, requireAuth, resolveEntityType, resolveEntityTypeId, toDraftValue]
  )

  const handleExecuteActions = useCallback(
    async (messageId: string, actions: AssistantAction[], editorValues?: AssistantEditorValueUpdate[]) => {
      if (!actions.length) return
      updateMessageById(messageId, (message) => ({ ...message, actionStatus: { status: 'running' } }))
      try {
        const createActionsWithIndex = actions
          .map((action, index) => ({ action, index }))
          .filter(({ action }) => action.type === 'create-entity')
        const remainingActions = actions.filter((action) => action.type !== 'create-entity')
        const placeholderMap = new Map<number, string>()

        for (const { action, index } of createActionsWithIndex) {
          const createdId = await executeAction(action)
          if (!createdId) throw new Error('Create action did not return an id.')
          placeholderMap.set(index + 1, createdId)
        }

        if (editorValues && editorValues.length > 0 && editorAdapter) {
          const resolvedValues = replacePlaceholdersInEditorValues(editorValues, placeholderMap)
          editorAdapter.applyEditorValues(resolvedValues)
        }

        const resolvedRemainingActions = remainingActions.map((action) =>
          replacePlaceholdersInAction(action, placeholderMap)
        )

        for (const action of resolvedRemainingActions) {
          await executeAction(action)
        }
        updateMessageById(messageId, (message) => ({ ...message, actionStatus: { status: 'success' } }))
        showSnackBar('Actions executed.', 'success')
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        updateMessageById(messageId, (message) => ({
          ...message,
          actionStatus: { status: 'error', error: errorMessage },
        }))
        showSnackBar(`Action failed: ${errorMessage}`, 'error')
      }
    },
    [editorAdapter, executeAction, showSnackBar, updateMessageById]
  )

  const refreshMcpStatus = async (force = false) => {
    setCheckingMcp(true)
    try {
      if (force) clearMcpStatusCache()
      const status = await getMcpStatus()
      setMcpReady(status.ready)
      setMcpMessage(
        status.ready
          ? `Tools available (${status.availableTools.join(', ')})`
          : `MCP tools unavailable${status.error ? `: ${status.error}` : ''}. Assistant will continue without tools.`
      )
    } catch (error) {
      setMcpReady(false)
      setMcpMessage(`Failed to check MCP tools: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setCheckingMcp(false)
    }
  }

  const openAssistantMenu = (event: ReactMouseEvent<HTMLElement>) => setAssistantMenuAnchor(event.currentTarget)
  const closeAssistantMenu = () => setAssistantMenuAnchor(null)
  const openModelMenu = () => setModelMenuOpen(true)
  const closeModelMenu = () => setModelMenuOpen(false)

  const clearAssistantHistory = () => {
    setAssistantMessages([])
    setAssistantInput('')
    closeAssistantMenu()
    closeModelMenu()
  }

  const handleAssistantSubmit = async () => {
    if (assistantSending) return
    const trimmed = assistantInput.trim()
    if (!trimmed) return

    const contextBlock = buildRequestContext(trimmed, assistantMessages)
    const messagesForApi = [{ role: 'user' as const, content: contextBlock }]
    const debugSession: AssistantDebugSession | undefined = debugMode
      ? {
          promptContext: contextBlock,
          systemPrompt: GLOBAL_ASSISTANT_STATIC_PROMPT,
          toolCallsRequested: [],
          toolCallsCompleted: [],
        }
      : undefined
    const createDebug = (error?: string): AssistantMessageDebug | undefined =>
      debugSession ? { session: debugSession, error } : undefined
    const userMessage: AssistantMessage = { id: createMessageId(), role: 'user', text: trimmed, debug: createDebug() }

    setAssistantMessages((previous) => [...previous, userMessage])
    setAssistantInput('')

    const startedAt = Date.now()
    assistantSendStartedAt.current = startedAt
    setAssistantElapsedMs(0)
    setAssistantSending(true)
    try {
      const result = await sendAssistantChat({
        messages: messagesForApi,
        model: selectedModel?.model,
        reasoningMode: selectedModel?.reasoningMode,
        systemPrompt: GLOBAL_ASSISTANT_STATIC_PROMPT,
        toolsEnabled: mcpReady === true,
        apiKey: openAiApiKey ?? undefined,
        onToolCallsRequested: (calls) => {
          if (calls.length === 0) return
          if (debugSession) {
            debugSession.toolCallsRequested = [...(debugSession.toolCallsRequested ?? []), ...calls]
          }
          const toolMessages = calls.map((call) => ({
            id: createMessageId(),
            role: 'assistant' as const,
            variant: 'tool-request' as const,
            text: formatToolCallMessage(call, false),
            debug: createDebug(),
          }))
          setAssistantMessages((previous) => [...previous, ...toolMessages])
        },
        onToolCallsCompleted: (calls) => {
          if (debugSession) {
            debugSession.toolCallsCompleted = [...(debugSession.toolCallsCompleted ?? []), ...calls]
          }
          const failedCalls = calls.filter((call) => !call.success)
          if (failedCalls.length === 0) return
          const toolMessages = failedCalls.map((call) => ({
            id: createMessageId(),
            role: 'assistant' as const,
            variant: 'error' as const,
            text: formatToolErrorMessage(call),
            debug: createDebug(call.error ?? 'Unknown error'),
          }))
          setAssistantMessages((previous) => [...previous, ...toolMessages])
        },
      })
      const newMessages: AssistantMessage[] = []

      const interimTexts =
        result.intermediateMessages ?? (result.intermediateMessage ? [result.intermediateMessage] : [])
      interimTexts.forEach((text) => {
        newMessages.push({
          id: createMessageId(),
          role: 'assistant',
          variant: 'intermediate',
          text: `Working: ${text}`,
          debug: createDebug(),
        })
      })

      if (debugSession) {
        debugSession.requestPayload = result.requestPayload
        debugSession.responsePayload = result.responsePayload
        debugSession.assistantMessage = result.assistantMessage
        if (result.toolCallsRequested) debugSession.toolCallsRequested = result.toolCallsRequested
        if (result.toolCalls) debugSession.toolCallsCompleted = result.toolCalls
      }

      const elapsedMs = Date.now() - startedAt
      const parsed = parseAssistantPayload(result.assistantMessage)
      if (!parsed.payload) {
        const errorMessage = parsed.error ?? 'Assistant response could not be parsed.'
        const text = `Error: ${errorMessage}`
        newMessages.push({
          id: createMessageId(),
          role: 'assistant',
          variant: 'error',
          text,
          debug: createDebug(errorMessage),
        })
        setAssistantMessages((previous) => [...previous, ...newMessages])
        return
      }

      const { userMessage: responseText, editorValues, actions } = parsed.payload
      const editorErrors: string[] = []
      const costLabel = formatCostLabel(result.usage, result.model)
      if (editorValues.length > 0) {
        if (editorAdapter) {
          const result = editorAdapter.applyEditorValues(editorValues)
          if (result.errors.length > 0) editorErrors.push(...result.errors)
        } else {
          editorErrors.push('No active editor is available to apply field values.')
        }
      }

      newMessages.push({
        id: createMessageId(),
        role: 'assistant',
        text: responseText,
        elapsedMs,
        costLabel,
        ...(actions.length > 0 ? { actions, actionStatus: { status: 'idle' as const } } : {}),
        ...(editorValues.length > 0 ? { editorValues } : {}),
        debug: createDebug(),
      })
      if (editorErrors.length > 0) {
        newMessages.push({
          id: createMessageId(),
          role: 'assistant',
          variant: 'error',
          text: `Could not apply editor updates: ${editorErrors.join('; ')}`,
          debug: createDebug(),
        })
      }
      setAssistantMessages((previous) => [...previous, ...newMessages])
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      setAssistantMessages((previous) => [
        ...previous,
        {
          id: createMessageId(),
          role: 'assistant',
          variant: 'error',
          text: `Error: ${errorMessage}`,
          debug: createDebug(errorMessage),
        },
      ])
    } finally {
      setAssistantSending(false)
      assistantSendStartedAt.current = null
      setAssistantElapsedMs(0)
    }
  }

  const clampAssistantRatio = useCallback(
    (value: number) => Math.min(ASSISTANT_MAX_RATIO, Math.max(ASSISTANT_MIN_RATIO, value)),
    []
  )

  const applyResizeFromClientY = useCallback(
    (clientY: number) => {
      const container = containerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      if (rect.height <= 0) return
      const relative = (clientY - rect.top) / rect.height
      setAssistantRatio(clampAssistantRatio(1 - relative))
    },
    [clampAssistantRatio]
  )

  const flushScheduledResize = useCallback(() => {
    resizeAnimationFrame.current = null
    const clientY = pendingResizeClientY.current
    if (clientY === null) return
    applyResizeFromClientY(clientY)
  }, [applyResizeFromClientY])

  const scheduleResizeFromClientY = useCallback(
    (clientY: number) => {
      pendingResizeClientY.current = clientY
      if (resizeAnimationFrame.current !== null) return
      resizeAnimationFrame.current = window.requestAnimationFrame(flushScheduledResize)
    },
    [flushScheduledResize]
  )

  const stopAssistantResize = useCallback(() => {
    activeResizePointerId.current = null
    pendingResizeClientY.current = null
    setAssistantResizing(false)
    if (resizeAnimationFrame.current !== null) {
      window.cancelAnimationFrame(resizeAnimationFrame.current)
      resizeAnimationFrame.current = null
    }
    if (previousUserSelect.current !== null) {
      document.body.style.userSelect = previousUserSelect.current
      previousUserSelect.current = null
    }
    if (previousCursor.current !== null) {
      document.body.style.cursor = previousCursor.current
      previousCursor.current = null
    }
  }, [])

  const startAssistantResize = useCallback(
    (pointerId: number, clientY: number) => {
      activeResizePointerId.current = pointerId
      setAssistantResizing(true)
      if (previousUserSelect.current === null) {
        previousUserSelect.current = document.body.style.userSelect
        document.body.style.userSelect = 'none'
      }
      if (previousCursor.current === null) {
        previousCursor.current = document.body.style.cursor
        document.body.style.cursor = 'row-resize'
      }
      scheduleResizeFromClientY(clientY)
    },
    [scheduleResizeFromClientY]
  )

  const handleResizeDividerPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      startAssistantResize(event.pointerId, event.clientY)
      if (typeof event.currentTarget.setPointerCapture === 'function') {
        event.currentTarget.setPointerCapture(event.pointerId)
      }
    },
    [startAssistantResize]
  )

  const handleResizeOverlayPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (activeResizePointerId.current !== null && event.pointerId !== activeResizePointerId.current) return
      event.preventDefault()
      event.stopPropagation()
      scheduleResizeFromClientY(event.clientY)
    },
    [scheduleResizeFromClientY]
  )

  const handleResizeOverlayPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (activeResizePointerId.current !== null && event.pointerId !== activeResizePointerId.current) return
      event.preventDefault()
      event.stopPropagation()
      stopAssistantResize()
    },
    [stopAssistantResize]
  )

  useEffect(() => {
    if (!assistantResizing) return

    const handlePointerMove = (event: PointerEvent) => {
      if (activeResizePointerId.current !== null && event.pointerId !== activeResizePointerId.current) return
      event.preventDefault()
      scheduleResizeFromClientY(event.clientY)
    }

    const handlePointerEnd = (event: PointerEvent) => {
      if (activeResizePointerId.current !== null && event.pointerId !== activeResizePointerId.current) return
      stopAssistantResize()
    }

    const handleBlur = () => {
      stopAssistantResize()
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: false })
    window.addEventListener('pointerup', handlePointerEnd)
    window.addEventListener('pointercancel', handlePointerEnd)
    window.addEventListener('blur', handleBlur)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerEnd)
      window.removeEventListener('pointercancel', handlePointerEnd)
      window.removeEventListener('blur', handleBlur)
    }
  }, [assistantResizing, scheduleResizeFromClientY, stopAssistantResize])

  useEffect(() => {
    return () => {
      if (resizeAnimationFrame.current !== null) {
        window.cancelAnimationFrame(resizeAnimationFrame.current)
        resizeAnimationFrame.current = null
      }
      if (previousUserSelect.current !== null) {
        document.body.style.userSelect = previousUserSelect.current
        previousUserSelect.current = null
      }
      if (previousCursor.current !== null) {
        document.body.style.cursor = previousCursor.current
        previousCursor.current = null
      }
    }
  }, [])

  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return
    container.scrollTop = container.scrollHeight
  }, [assistantMessages, assistantSending, assistantOpen])

  useEffect(() => {
    if (assistantOpen) void refreshMcpStatus()
  }, [assistantOpen])

  useEffect(() => {
    if (!assistantOpen && assistantResizing) {
      stopAssistantResize()
    }
  }, [assistantOpen, assistantResizing, stopAssistantResize])

  useEffect(() => {
    if (!assistantSending) return
    const interval = window.setInterval(() => {
      const startedAt = assistantSendStartedAt.current
      if (!startedAt) return
      setAssistantElapsedMs(Date.now() - startedAt)
    }, 1000)
    return () => {
      window.clearInterval(interval)
    }
  }, [assistantSending])

  const debugSession = debugDialogData?.session
  const debugPromptPayload =
    debugSession?.requestPayload ??
    (debugSession
      ? {
          systemPrompt: debugSession.systemPrompt,
          userContext: debugSession.promptContext,
        }
      : undefined)
  const debugResponsePayload =
    debugSession?.responsePayload ??
    (debugSession?.assistantMessage ? { assistantMessage: debugSession.assistantMessage } : undefined)
  const debugToolPayload = debugSession
    ? {
        requested: debugSession.toolCallsRequested ?? [],
        completed: debugSession.toolCallsCompleted ?? [],
      }
    : undefined
  const clampedAssistantRatio = clampAssistantRatio(assistantRatio)

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateRows: assistantOpen
            ? `${Math.max(ASSISTANT_MIN_RATIO, 1 - clampedAssistantRatio)}fr ${ASSISTANT_DIVIDER_SIZE_PX}px ${clampedAssistantRatio}fr`
            : '1fr',
          height: 'calc(100vh - 64px)',
          minHeight: 300,
        }}
      >
        <Box sx={{ minHeight: 0, overflow: 'auto' }}>{children}</Box>
        {assistantOpen && (
          <>
            <Box
              role="separator"
              aria-orientation="horizontal"
              sx={{
                cursor: 'row-resize',
                bgcolor: 'divider',
                touchAction: 'none',
              }}
              onPointerDown={handleResizeDividerPointerDown}
            />
            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <Stack
                spacing={1}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  pr: 2,
                  mr: -1,
                  scrollbarGutter: 'stable both-edges',
                  scrollBehavior: 'smooth',
                }}
                ref={messagesContainerRef}
              >
                {(assistantToolNotice || checkingMcp || mcpReady === false || showStatus) && (
                  <Stack direction="row" justifyContent="flex-start">
                    <Paper
                      variant="outlined"
                      sx={{
                        px: 1.5,
                        py: 1,
                        maxWidth: '80%',
                        bgcolor: 'background.paper',
                        borderColor: mcpReady === false ? 'error.light' : 'divider',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        {checkingMcp && <Chip size="small" label="Checking tools..." />}
                        {assistantToolNotice && <Chip size="small" color="primary" label={assistantToolNotice} />}
                        {mcpReady === false && <Chip size="small" color="warning" label="Tools unavailable" />}
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {mcpMessage}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                )}
                {assistantMessages.map((message) => {
                  const isUser = message.role === 'user'
                  const isIntermediate = message.variant === 'intermediate'
                  const isTool = message.variant === 'tool-request' || message.variant === 'tool-result'
                  const isErrorVariant = message.variant === 'error'
                  const showDuration =
                    message.role === 'assistant' && !message.variant && message.elapsedMs !== undefined
                  const isAnswer = message.role === 'assistant' && !message.variant
                  const costLabel = message.costLabel
                  const actions = message.actions ?? []
                  const actionStatus = message.actionStatus
                  const canOpenDebug = debugMode && !!message.debug
                  const background = isUser
                    ? 'primary.light'
                    : isErrorVariant
                      ? 'error.main'
                      : isTool
                        ? 'grey.100'
                        : isIntermediate
                          ? 'grey.100'
                          : 'background.paper'
                  const textColor = isUser
                    ? 'text.primary'
                    : isErrorVariant
                      ? 'error.contrastText'
                      : isTool
                        ? 'text.secondary'
                        : 'text.primary'
                  return (
                    <Stack key={message.id} direction="row" justifyContent={isUser ? 'flex-end' : 'flex-start'}>
                      <Paper
                        variant="outlined"
                        onClick={canOpenDebug ? () => openDebugDialog(message) : undefined}
                        sx={{
                          px: 1.5,
                          py: 1,
                          maxWidth: '80%',
                          bgcolor: background,
                          cursor: canOpenDebug ? 'pointer' : 'default',
                        }}
                      >
                        {isAnswer ? (
                          <Typography
                            variant="body2"
                            color={textColor}
                            component="div"
                            sx={assistantMarkdownStyles}
                            dangerouslySetInnerHTML={{ __html: renderAssistantMarkdown(message.text) }}
                          />
                        ) : (
                          <Typography variant="body2" color={textColor} sx={{ whiteSpace: 'pre-wrap' }}>
                            {message.text}
                          </Typography>
                        )}
                        {actions.length > 0 && (
                          <Stack spacing={0.75} sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Proposed actions
                            </Typography>
                            {actions.map((action, actionIndex) => (
                              <Stack key={`${message.id}-action-${actionIndex}`} spacing={0.25}>
                                <Typography variant="body2">
                                  {`${actionIndex + 1}. ${action.summary ?? action.type}`}
                                </Typography>
                                {action.reason && (
                                  <Typography variant="caption" color="text.secondary">
                                    {action.reason}
                                  </Typography>
                                )}
                              </Stack>
                            ))}
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={(event) => {
                                  event.stopPropagation()
                                  handleExecuteActions(message.id, actions, message.editorValues)
                                }}
                                disabled={actionStatus?.status === 'running' || actionStatus?.status === 'success'}
                              >
                                {actionStatus?.status === 'running' ? 'Executing...' : 'Execute'}
                              </Button>
                              {actionStatus?.status === 'success' && (
                                <Typography variant="caption" color="success.main">
                                  Actions executed.
                                </Typography>
                              )}
                              {actionStatus?.status === 'error' && (
                                <Typography variant="caption" color="error.main">
                                  {actionStatus.error ?? 'Action failed.'}
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        )}
                        {showDuration && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', fontSize: '0.65rem', lineHeight: 1.1, textAlign: 'right' }}
                          >
                            Took {formatElapsedTime(message.elapsedMs ?? 0)}
                            {costLabel ? ` | ${costLabel}` : ''}
                          </Typography>
                        )}
                      </Paper>
                    </Stack>
                  )
                })}
                {assistantSending && (
                  <Stack direction="row" justifyContent="flex-start">
                    <Paper
                      variant="outlined"
                      sx={{
                        px: 1.5,
                        py: 1,
                        maxWidth: '80%',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        Thinking... ({formatElapsedTime(assistantElapsedMs)})
                      </Typography>
                    </Paper>
                  </Stack>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 65, height: 40, flexShrink: 0 }} />
                <TextField
                  label="Ask the assistant"
                  multiline
                  minRows={1}
                  maxRows={4}
                  fullWidth
                  size="small"
                  value={assistantInput}
                  onChange={(event) => setAssistantInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && event.shiftKey) {
                      event.preventDefault()
                      const target = assistantInputRef.current
                      const start = target?.selectionStart ?? assistantInput.length
                      const end = target?.selectionEnd ?? assistantInput.length
                      const next =
                        assistantInput.slice(0, start) + '\n' + assistantInput.slice(end, assistantInput.length)
                      setAssistantInput(next)
                      requestAnimationFrame(() => {
                        const el = assistantInputRef.current
                        if (el) el.setSelectionRange(start + 1, start + 1)
                      })
                      return
                    }
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault()
                      void handleAssistantSubmit()
                    }
                  }}
                  inputProps={{ sx: { py: 0.75 }, title: '', 'aria-label': 'Ask the assistant' }}
                  autoComplete="off"
                  inputRef={assistantInputRef}
                />
                <IconButton
                  aria-label="Assistant options"
                  onClick={openAssistantMenu}
                  sx={{ alignSelf: 'center' }}
                  disabled={assistantSending}
                >
                  <MoreVert />
                </IconButton>
                <Menu anchorEl={assistantMenuAnchor} open={!!assistantMenuAnchor} onClose={closeAssistantMenu}>
                  <MenuItem
                    ref={modelMenuItemRef}
                    disableRipple
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      openModelMenu()
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        openModelMenu()
                      }
                    }}
                    sx={{ pr: 1 }}
                  >
                    <ListItemText primary="Model" />
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ChevronRight fontSize="small" />
                    </ListItemIcon>
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      void refreshMcpStatus(true)
                      closeAssistantMenu()
                    }}
                    disabled={checkingMcp}
                  >
                    <ListItemText primary="Refresh Tools" />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      clearAssistantHistory()
                    }}
                  >
                    <ListItemText primary="Clear History" />
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSettingsDialogOpen(true)
                      closeAssistantMenu()
                    }}
                  >
                    <ListItemText primary="Settings" />
                  </MenuItem>
                </Menu>
                <Menu
                  anchorEl={modelMenuItemRef.current}
                  open={modelMenuOpen}
                  onClose={closeModelMenu}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  MenuListProps={{ onMouseLeave: closeModelMenu, autoFocus: false }}
                >
                  {modelOptions.map((option) => {
                    const isSelected = selectedModel?.id === option.id
                    return (
                      <MenuItem
                        key={option.id}
                        selected={isSelected}
                        onClick={() => {
                          setSelectedModelId(option.id)
                          closeModelMenu()
                          closeAssistantMenu()
                        }}
                      >
                        <ListItemIcon>{isSelected ? <Check fontSize="small" /> : <Box width={24} />}</ListItemIcon>
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    )
                  })}
                </Menu>
              </Stack>
            </Paper>
          </>
        )}
      </Box>
      {assistantResizing && (
        <Box
          onPointerMove={handleResizeOverlayPointerMove}
          onPointerUp={handleResizeOverlayPointerUp}
          onPointerCancel={handleResizeOverlayPointerUp}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: (theme) => theme.zIndex.tooltip + 1,
            cursor: 'row-resize',
            touchAction: 'none',
            backgroundColor: 'transparent',
          }}
        />
      )}
      <Box
        sx={{
          position: 'fixed',
          left: `${Math.max(0, _drawerWidth) + 24}px`,
          bottom: 24,
          zIndex: (theme) => theme.zIndex.drawer + 3,
        }}
      >
        <Fab
          size="small"
          color="primary"
          aria-label={assistantOpen ? 'Hide assistant' : 'Show assistant'}
          onClick={() => setAssistantOpen((open) => !open)}
        >
          {assistantOpen ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
        </Fab>
      </Box>
      <Dialog open={debugDialogOpen} onClose={closeDebugDialog} maxWidth="md" fullWidth>
        <DialogTitle>Debug Details</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {debugDialogData?.error && (
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Error</Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    bgcolor: 'grey.50',
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {debugDialogData.error}
                </Box>
              </Stack>
            )}
            {debugSession && (
              <>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">Prompt</Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      bgcolor: 'grey.50',
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatDebugValue(debugPromptPayload)}
                  </Box>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">Response</Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      bgcolor: 'grey.50',
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatDebugValue(debugResponsePayload)}
                  </Box>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">MCP Calls</Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      bgcolor: 'grey.50',
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {formatDebugValue(debugToolPayload)}
                  </Box>
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDebugDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      <UserSettingsDialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} />
    </>
  )
}

export type { AssistantMessage, AssistentProps, ModelOption }
export default Assistent
